// What the robot's own camera sees, over the corner of the 3D view.
//
// Nothing here is a picture of the simulation: it is the *same* three.js scene
// drawn a second time, from the pose of the link the sensor is bolted to and
// through that sensor's own field of view. So it shows exactly what the main
// view shows -- the same meshes, the same ground, the same lighting -- from an
// eye the user cannot orbit.
//
// A second `WebGLRenderer` rather than a corner of the main one. Sharing a
// scene between two renderers is what three.js has them for, and it buys a
// plain DOM panel that can be sized to the sensor's aspect ratio and collapsed
// out of the way, instead of a rectangle scissored into a canvas the orbit
// controls own. The cost is a second GL context and a second upload of every
// geometry, which is why the loop below stops dead when the panel is collapsed
// and why the image is redrawn at the sensor's frame rate rather than the
// display's.
//
// The emulated board reads it too. When the robot runs an ESP32-S3 and its
// firmware brings up the camera driver, the controller asks for frames --
// `camera_request_at` says what the sensor was configured to stream -- and
// the loop below renders that view offscreen at the requested size and hands
// the pixels in through `supply_camera_frame_at`. That capture is the
// firmware's, not the panel's: it runs whether or not the panel is collapsed,
// which is why collapsing now stops only the visible redraw rather than the
// whole loop.
//
// The picture can be turned a quarter turn at a time, for a sensor mounted on
// its side or upside down. That turns the panel and nothing else: the board is
// fed the frame the link's own axes give, as a real sensor would be. How far each view is turned is kept by link name so the saved
// configuration can carry it (`cameraViewRotations`).

import { AddedRobot, InterfaceHybrid } from "simulator";
import { Simulator } from "gorilla-physics-ui";
import {
  Mesh,
  PerspectiveCamera,
  Quaternion,
  Vector3,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";

// How wide the image is drawn, in CSS pixels. Its height comes from the
// sensor's own aspect ratio, so a 4:3 array is drawn 4:3.
const VIEW_WIDTH = 212;

// Nothing closer than a centimetre. A lens sits behind a few millimetres of
// housing and the link it is bolted to is hidden below anyway, so this is only
// here to keep the depth buffer sane over a scene measured in metres.
const NEAR = 0.01;
const FAR = 1000;

// The layer the camera's own link is moved onto: drawn in the main view, never
// in the camera's. A sensor is at the origin of its link, so a link with a body
// of its own -- a camera module, a mount -- would otherwise fill the image with
// the inside of itself.
const OWN_LINK_LAYER = 1;

// The camera link's axes as three.js wants them.
//
// `camera_optical` promises the optical-frame convention: +Z is the direction
// it looks, +X is right across the image and +Y is *down* it. A three.js camera
// looks down its own -Z with +Y up, so the two differ by a half turn about X --
// which flips the direction of view and the direction of up together, and is
// the whole of the conversion.
const OPTICAL = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), Math.PI);

// The axis a three.js camera looks back along. Rolling the eye about it
// counter-clockwise turns the picture it draws clockwise.
const VIEW_AXIS = new Vector3(0, 0, 1);

// Linear-light byte -> sRGB byte.
//
// three.js applies `outputColorSpace` only on the way to the canvas: for a
// render target it hardcodes LinearSRGBColorSpace, whatever the target's own
// texture says (`three.module.js`, the `outputColorSpace:` ternary in
// WebGLPrograms). So the panel's picture is encoded and the offscreen capture
// below is not, and the frames the firmware sent came out roughly a 2.2 gamma
// too dark -- a 0x292929 background readback as 6, not 41. Setting
// `target.texture.colorSpace` does not fix it on this version; encoding the
// readback does.
const SRGB = (() => {
  const table = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    const s = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    table[i] = Math.round(s * 255);
  }
  return table;
})();

// One camera on the robot being watched: where to read its pose from, and what
// it sees when it looks.
interface View {
  link: string;
  model: string;
  // Index into the articulated's bodies, which is what its pose is read off.
  body: number;
  fovDegrees: number;
  width: number;
  height: number;
  fps: number;
  // How many quarter turns clockwise the panel draws it at, 0 to 3.
  quarterTurns: number;
}

let simulator: Simulator | undefined;
let hybrid: InterfaceHybrid | undefined;
// Which articulated is being looked at -- as with the editor and the switches,
// the most recently added robot.
let robot = 0;
let views: View[] = [];
let showing = 0;
let collapsed = false;

let renderer: WebGLRenderer | undefined;
let camera: PerspectiveCamera | undefined;
// The panel eye's roll about its own view axis, from the showing view's
// `quarterTurns`. Applied after `OPTICAL`, so it turns the picture and not the
// direction the camera looks.
let roll = new Quaternion();
let frame: number | undefined;
let lastDrawn = 0;
// The meshes moved onto `OWN_LINK_LAYER`, so they can be put back when the
// panel is pointed at another robot.
let hiddenMeshes: Mesh[] = [];

// The offscreen render each emulated camera is fed from: a target at the
// exact size the firmware configured its sensor to, a projection through the
// same lens the panel draws with, and the buffers the pixels come back in.
// Kept per view, remade whenever a re-flashed sketch asks for another size.
interface Capture {
  target: WebGLRenderTarget;
  camera: PerspectiveCamera;
  width: number;
  height: number;
  // `set_vflip` reverses the scan-line order on the sensor before DVP.
  verticalFlip: boolean;
  // Read straight off the GPU (bottom row first), and the top-first copy the
  // sensor wants -- kept to avoid two allocations per frame at 15 fps.
  readback: Uint8Array;
  flipped: Uint8Array;
  lastFed: number;
}
let captures = new Map<number, Capture>();

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

// Point the panel at the robot just added. One with no camera link -- or one
// whose camera links were left without a sensor -- hides it, on the same terms
// as the switch panel: an empty view claims something, and the wrong thing.
//
// `rotations` is how far to turn each view, in degrees clockwise and keyed by
// link: what a saved configuration asked for, or what the robot being rebuilt
// was showing. A link it says nothing about is drawn upright.
export function showCameraViewOf(
  scene: Simulator,
  state: InterfaceHybrid,
  added: AddedRobot,
  rotations: Map<string, number> = new Map(),
) {
  // Whatever was hidden belonged to the robot being replaced, and it is still
  // in the scene: hand its meshes back before losing track of them.
  revealOwnLinks();

  // The capture targets sized themselves to the old robot's sensors.
  for (const capture of captures.values()) {
    capture.target.dispose();
  }
  captures = new Map();

  simulator = scene;
  hybrid = state;
  robot = added.index;
  views = [];
  for (let c = 0; c < added.cameraCount; c++) {
    const resolution = added.cameraResolution(c);
    const body = added.cameraBody(c);
    const fov = added.cameraFovDegrees(c);
    if (body === undefined || fov === undefined || resolution.length < 2) {
      continue;
    }
    const link = added.cameraLink(c) ?? `camera ${c}`;
    views.push({
      link,
      model: added.cameraModel(c) ?? "camera",
      body,
      fovDegrees: fov,
      width: resolution[0],
      height: resolution[1],
      fps: added.cameraFps(c) ?? 30,
      quarterTurns: quarterTurnsOf(rotations.get(link) ?? 0),
    });
  }

  const panel = element("camera-view");
  panel.hidden = views.length === 0;
  if (views.length === 0) {
    stop();
    return;
  }

  wire();
  fillPicker();
  select(0);
}

// How far each view is turned, in degrees clockwise and keyed by link -- for
// the saved configuration, and for a rebuild to carry over. Every view is in
// it, upright ones as 0, so a file says what the panel showed rather than
// leaving a reader to assume.
export function cameraViewRotations(): Map<string, number> {
  return new Map(views.map((view) => [view.link, view.quarterTurns * 90]));
}

// Degrees to the nearest quarter turn, 0 to 3. Anything that is not a number
// is upright.
function quarterTurnsOf(degrees: number): number {
  if (!Number.isFinite(degrees)) {
    return 0;
  }
  return ((Math.round(degrees / 90) % 4) + 4) % 4;
}

// Every camera the robot has, so a robot with one is a label and a robot with
// several is a choice. Named by link, which is what tells two of them apart.
function fillPicker() {
  const pick = element<HTMLSelectElement>("camera-pick");
  pick.replaceChildren(
    ...views.map((view, index) => new Option(view.link, String(index))),
  );
  // Collapsed hides everything under the header, and a robot dropped while it
  // is collapsed must not push the picker back out on its own.
  pick.hidden = collapsed || views.length < 2;
}

// Show one of them: resize the canvas to its array's shape, build a projection
// from its lens, and hide the link it is bolted to.
function select(index: number) {
  const view = views[index];
  if (!view || !simulator) {
    return;
  }
  showing = index;
  element<HTMLSelectElement>("camera-pick").value = String(index);

  const canvas = element<HTMLCanvasElement>("camera-canvas");

  if (!renderer) {
    renderer = new WebGLRenderer({ antialias: true, canvas });
    // The same clear colour the main view uses, so the two read as one scene
    // rather than as a picture pasted over it.
    renderer.setClearColor(0x292929, 1);
    renderer.setPixelRatio(
      window.devicePixelRatio ? Math.min(window.devicePixelRatio, 1.5) : 1,
    );
  }
  project(view);

  revealOwnLinks();
  hideOwnLink(view);

  element("camera-view-title").textContent = view.link;
  // The part, the array it reads out, and the axis it looks down. The last is
  // the one nothing in the picture could tell the user: a link drawn with its
  // +Z somewhere unintended looks like a camera pointed at the floor, and this
  // is what says why.
  const fps = view.fps.toFixed(0);
  element("camera-view-note").textContent =
    `${view.model} · ${view.width}×${view.height} · ${fps} fps · ` +
    `looks down +Z of \`${view.link}\``;

  render(performance.now());
  start();
}

// Size the canvas and build the panel's projection for `view` as it is turned.
//
// On its side the picture is the array's shape stood on end, and what runs up
// the panel is what ran across the sensor -- so the projection takes the
// horizontal field of view as its vertical one. Rolling the eye then puts the
// same frustum on screen a quarter turn round, with nothing cropped.
function project(view: View) {
  if (!renderer) {
    return;
  }
  const sideways = view.quarterTurns % 2 === 1;
  const aspect = sideways
    ? view.height / view.width
    : view.width / view.height;
  renderer.setSize(VIEW_WIDTH, Math.round(VIEW_WIDTH / aspect));

  const halfFov = (view.fovDegrees * Math.PI) / 360;
  const fov = sideways
    ? (360 / Math.PI) * Math.atan(Math.tan(halfFov) * (view.width / view.height))
    : view.fovDegrees;
  camera = new PerspectiveCamera(fov, aspect, NEAR, FAR);
  // Everything except the link this camera is on, which `hideOwnLink` moves out
  // of layer 0.
  camera.layers.set(0);
  roll = new Quaternion().setFromAxisAngle(
    VIEW_AXIS,
    (view.quarterTurns * Math.PI) / 2,
  );
}

// Take the camera's own link out of its view. The mesh stays in the scene and
// in the main view -- it is only moved to a layer this camera does not draw,
// which the main camera is told to draw as well.
function hideOwnLink(view: View) {
  if (!simulator || !hybrid) {
    return;
  }
  simulator.graphics.camera.layers.enable(OWN_LINK_LAYER);
  const frameName = hybrid.frame_articulated_body(robot, view.body);
  const visuals = hybrid.n_visuals_articulated_body(robot, view.body);
  for (let k = 0; k < visuals; k++) {
    // The name `addArticulatedVisuals` gave it, which is the only handle on a
    // link's meshes from out here.
    const mesh = simulator.meshes.get(`${frameName}-${k}`);
    if (mesh) {
      mesh.layers.set(OWN_LINK_LAYER);
      hiddenMeshes.push(mesh);
    }
  }
}

function revealOwnLinks() {
  for (const mesh of hiddenMeshes) {
    mesh.layers.set(0);
  }
  hiddenMeshes = [];
}

// Redraw at the sensor's frame rate rather than the display's. It is both what
// the part does -- an OV3660 reads its full array out 15 times a second -- and
// what keeps a second render of the whole scene off three frames in four.
//
// The board's captures come first and run regardless of the panel: what the
// firmware sees does not depend on what the user is looking at.
function tick(now: number) {
  frame = requestAnimationFrame(tick);
  feedBoard(now);
  const view = views[showing];
  if (!view || collapsed) {
    return;
  }
  if (now - lastDrawn < 1000 / view.fps) {
    return;
  }
  lastDrawn = now;
  render(now);
}

// Give the emulated board whatever frames its firmware is asking for.
//
// Polled rather than told: what a sensor streams is programmed by the running
// sketch (`esp_camera_init`), so the request appears when the sketch runs and
// changes when a new one is flashed. No request, no work -- a robot whose
// firmware never touches the camera costs one wasm call per view per frame.
function feedBoard(now: number) {
  if (!hybrid) {
    return;
  }
  for (let c = 0; c < views.length; c++) {
    let request: Uint32Array;
    try {
      request = hybrid.camera_request_at(robot, c);
    } catch (error) {
      stop();
      console.error("camera: stopped feeding the board", error);
      return;
    }
    if (request.length < 4) {
      continue;
    }
    const [, width, height, fps, verticalFlip] = request;
    const capture = captureFor(c, width, height, verticalFlip !== 0);
    if (!capture) {
      continue;
    }
    if (now - capture.lastFed < 1000 / Math.max(fps, 1)) {
      continue;
    }
    capture.lastFed = now;
    captureView(c, capture);
  }
}

// The capture state for view `c` at the requested size, remade if the
// firmware asked for a different one -- a re-flashed sketch is free to pick
// another framesize.
function captureFor(
  c: number,
  width: number,
  height: number,
  verticalFlip: boolean,
): Capture | undefined {
  const view = views[c];
  if (!view || !renderer) {
    return undefined;
  }
  let capture = captures.get(c);
  if (
    capture &&
    (capture.width !== width ||
      capture.height !== height ||
      capture.verticalFlip !== verticalFlip)
  ) {
    capture.target.dispose();
    capture = undefined;
  }
  if (!capture) {
    const eye = new PerspectiveCamera(
      view.fovDegrees,
      width / height,
      NEAR,
      FAR,
    );
    // The same layer discipline as the panel's camera. The mesh moved off
    // layer 0 is the *selected* view's own link, so a robot with several
    // cameras may catch another camera's housing in shot -- as the panel
    // itself would if it could show two at once.
    eye.layers.set(0);
    capture = {
      target: new WebGLRenderTarget(width, height),
      camera: eye,
      width,
      height,
      verticalFlip,
      readback: new Uint8Array(width * height * 4),
      flipped: new Uint8Array(width * height * 4),
      lastFed: 0,
    };
    captures.set(c, capture);
  }
  return capture;
}

// One frame for the board: the scene through view `c`'s eye, at the sensor's
// configured size, as top-row-first RGBA.
function captureView(c: number, capture: Capture) {
  const view = views[c];
  if (!view || !simulator || !hybrid || !renderer) {
    return;
  }
  try {
    const pose = hybrid.pose_articulated_body(robot, view.body);
    capture.camera.position.set(pose[4], pose[5], pose[6]);
    capture.camera.quaternion
      .set(pose[0], pose[1], pose[2], pose[3])
      .multiply(OPTICAL);

    // Lit from the capturing eye, exactly as `render` lights the panel; the
    // next main render puts the light back on its own.
    simulator.graphics.light.position.copy(capture.camera.position);
    renderer.setRenderTarget(capture.target);
    renderer.render(simulator.graphics.scene, capture.camera);
    renderer.readRenderTargetPixels(
      capture.target,
      0,
      0,
      capture.width,
      capture.height,
      capture.readback,
    );
    renderer.setRenderTarget(null);

    // GL hands rows back bottom-first; an unflipped sensor scans top-first,
    // while `set_vflip` reverses its scan-line order before DVP. The bytes are
    // linear-light, which `SRGB` above is the whole explanation of.
    // Both are per-byte work on a buffer already being copied, so they share
    // the one pass; alpha carries no transfer function and is copied as it is.
    const stride = capture.width * 4;
    for (let row = 0; row < capture.height; row++) {
      const from = row * stride;
      const sensorRow = capture.verticalFlip ? row : capture.height - 1 - row;
      const to = sensorRow * stride;
      for (let x = 0; x < stride; x += 4) {
        capture.flipped[to + x] = SRGB[capture.readback[from + x]];
        capture.flipped[to + x + 1] = SRGB[capture.readback[from + x + 1]];
        capture.flipped[to + x + 2] = SRGB[capture.readback[from + x + 2]];
        capture.flipped[to + x + 3] = capture.readback[from + x + 3];
      }
    }
    hybrid.supply_camera_frame_at(robot, c, capture.flipped);
  } catch (error) {
    stop();
    console.error("camera: stopped feeding the board", error);
  }
}

function render(now: number) {
  const view = views[showing];
  if (!view || !simulator || !hybrid || !renderer || !camera) {
    return;
  }
  try {
    const pose = hybrid.pose_articulated_body(robot, view.body);
    camera.position.set(pose[4], pose[5], pose[6]);
    camera.quaternion
      .set(pose[0], pose[1], pose[2], pose[3])
      .multiply(OPTICAL)
      .multiply(roll);

    // The scene has one point light and `Graphics.render` parks it on the orbit
    // camera every frame. Doing the same here means each render is lit from
    // whichever eye is drawing, instead of the camera looking into the shadow
    // the main view has moved the light out of. Nothing needs restoring: the
    // main render sets it back on its own next frame.
    simulator.graphics.light.position.copy(camera.position);
    renderer.render(simulator.graphics.scene, camera);
  } catch (error) {
    // As with the serial and switch polls: wasm is unusable after a panic, and
    // a dead scene should not repaint itself fifteen times a second forever.
    stop();
    console.error("camera: stopped drawing", error);
  }
  lastDrawn = now;
}

// The loop runs whenever the robot has cameras at all, collapsed or not: the
// panel redraw is skipped while hidden, but the firmware's captures are not
// the user's to pause -- a sketch mid-`esp_camera_fb_get` is owed its frames
// either way.
function start() {
  if (frame === undefined) {
    frame = requestAnimationFrame(tick);
  }
}

function stop() {
  if (frame !== undefined) {
    cancelAnimationFrame(frame);
    frame = undefined;
  }
}

// The collapse and rotate buttons and the picker, wired once. The panel itself is never
// removed from the page, so neither are these.
function wire() {
  const toggle = element<HTMLButtonElement>("camera-view-toggle");
  if (toggle.dataset.wired === "true") {
    return;
  }
  toggle.dataset.wired = "true";

  toggle.addEventListener("click", () => {
    collapsed = !collapsed;
    element("camera-canvas").hidden = collapsed;
    element("camera-view-note").hidden = collapsed;
    element("camera-pick").hidden = collapsed || views.length < 2;
    element("camera-view-rotate").hidden = collapsed;
    toggle.textContent = collapsed ? "Show" : "Hide";
    // Collapsed only hides the picture. The loop keeps running because the
    // *board* may still be taking frames through `feedBoard`; `tick` skips
    // the panel redraw itself, so a collapsed panel with an idle camera
    // costs a poll per frame and nothing more.
  });

  // A quarter turn clockwise for the view showing. Only the panel's projection
  // is rebuilt: the link is still the one hidden, and the board's capture
  // never turned.
  element("camera-view-rotate").addEventListener("click", () => {
    const view = views[showing];
    if (!view) {
      return;
    }
    view.quarterTurns = (view.quarterTurns + 1) % 4;
    project(view);
    render(performance.now());
  });

  element<HTMLSelectElement>("camera-pick").addEventListener("change", (event) =>
    select(Number((event.target as HTMLSelectElement).value)),
  );
}
